import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Layout,
  Page,
  ProgressBar,
  Text,
  TextField
} from "@shopify/polaris";
import { useEffect, useMemo, useState } from "react";
import { ApiError, apiJson, bootstrapShopifySession, downloadFile, isShopifyEmbeddedApp } from "./api";

type ScanJob = {
  id: string;
  status: "queued" | "running" | "completed" | "failed";
  scan_mode: string;
  bulk_operation_id: string | null;
  bulk_operation_status: string | null;
  bulk_object_count: number;
  bulk_file_size: number | null;
  total_products: number;
  total_variants: number;
  total_issues: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  finished_at: string | null;
};

type Issue = {
  id: string;
  severity: "critical" | "warning" | "info";
  status: "open" | "ignored" | "resolved";
  issue_type: string;
  entity_type: string;
  product_title: string | null;
  variant_title: string | null;
  sku: string | null;
  field: string;
  message: string;
  suggested_fix: string;
  note: string | null;
  actioned_at: string | null;
};

type ExportRecord = {
  id: string;
  filename: string;
  created_at: string;
  scan_job_id: string;
};

type MeResponse = {
  shop: {
    domain: string;
    name: string | null;
    currency: string | null;
    scope: string;
    installedAt: string;
  } | null;
  latestScan: ScanJob | null;
  capabilities: {
    scanMode: string;
    readOnly: boolean;
    maxExportIssues: number;
    maxBulkPollMinutes: number;
    supportsIssueWorkflow: boolean;
    supportsFieldMapping: boolean;
    supportsPrivacyWebhooks: boolean;
    supportsBilling: boolean;
    canExport: boolean;
    productLimit: number | null;
  };
  billing: BillingSummary;
};

type BillingPlanKey = "free" | "pro";

type BillingPlan = {
  key: BillingPlanKey;
  name: string;
  price: number;
  currencyCode: "USD";
  interval: "EVERY_30_DAYS";
  trialDays: number;
  productLimit: number | null;
  canExport: boolean;
  features: string[];
};

type BillingSummary = {
  plan: BillingPlanKey;
  status: "free" | "active" | "pending" | "frozen";
  activeSubscription: {
    id: string;
    name: string;
    status: string;
    test: boolean;
    trialDays: number;
    currentPeriodEnd: string | null;
  } | null;
  billingMode: "app_pricing" | "billing_api";
  canManageInApp: boolean;
  planSelectionUrl: string | null;
  testMode: boolean;
  plans: BillingPlan[];
  limits: {
    productLimit: number | null;
    canExport: boolean;
    maxExportIssues: number;
  };
};

type IssueFacets = {
  severity: {
    critical: number;
    warning: number;
    info: number;
  };
  issueTypes: Array<{
    issue_type: string;
    count: number;
  }>;
};

type FeedFieldKey = "brand" | "mpn" | "identifierExists" | "color" | "size" | "gender" | "ageGroup";

type FeedMappingSettings = Record<FeedFieldKey, string[]>;

type View = "dashboard" | "issues" | "exports" | "settings" | "billing";

const FIELD_LABELS: Record<FeedFieldKey, string> = {
  brand: "Brand",
  mpn: "MPN",
  identifierExists: "Identifier exists",
  color: "Color",
  size: "Size",
  gender: "Gender",
  ageGroup: "Age group"
};

export function App() {
  const [view, setView] = useState<View>("dashboard");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [scans, setScans] = useState<ScanJob[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState("all");
  const [issueType, setIssueType] = useState("all");
  const [status, setStatus] = useState("open");
  const [query, setQuery] = useState("");
  const [issueFacets, setIssueFacets] = useState<IssueFacets>(emptyIssueFacets());
  const [settings, setSettings] = useState<FeedMappingSettings | null>(null);
  const [settingsDraft, setSettingsDraft] = useState<FeedMappingSettings>(emptyFeedMappings());
  const [savingSettings, setSavingSettings] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);

  const activeScan = me?.latestScan ?? scans[0] ?? null;
  const isScanning = activeScan?.status === "queued" || activeScan?.status === "running";
  const stats = issueFacets.severity;
  const issueTypeOptions = useMemo(() => issueFacets.issueTypes, [issueFacets]);
  const embeddedInShopify = isShopifyEmbeddedApp();
  const installShop = shopFromInstallUrl();

  useEffect(() => {
    if (!embeddedInShopify && installShop) {
      window.location.assign(`/auth/start?shop=${encodeURIComponent(installShop)}`);
      return;
    }
    void refreshAll();
  }, []);

  useEffect(() => {
    if (!isScanning) return;
    const timer = window.setInterval(() => void refreshAll({ silent: true }), 2500);
    return () => window.clearInterval(timer);
  }, [isScanning]);

  async function refreshAll(options: { silent?: boolean } = {}) {
    if (!options.silent) setLoading(true);
    setError(null);
    try {
      if (!me?.shop && embeddedInShopify) {
        await bootstrapShopifySession();
      }
      const nextMe = await apiJson<MeResponse>("/api/me");
      const nextScans = await apiJson<{ scans: ScanJob[] }>("/api/scans");
      const nextSettings = await apiJson<{ settings: FeedMappingSettings }>("/api/settings");
      setMe(nextMe);
      setScans(nextScans.scans);
      setSettings(nextSettings.settings);
      setSettingsDraft(nextSettings.settings);
      const scanId = nextMe.latestScan?.id ?? nextScans.scans[0]?.id;
      if (scanId) {
        const [nextIssues, nextFacets] = await Promise.all([
          fetchIssues(scanId, severity, issueType, status, query),
          apiJson<{ facets: IssueFacets }>(`/api/issues/facets?scanId=${encodeURIComponent(scanId)}&status=${encodeURIComponent(status)}`)
        ]);
        setIssues(nextIssues.issues);
        setIssueFacets(nextFacets.facets);
      } else {
        setIssues([]);
        setIssueFacets(emptyIssueFacets());
      }
      const nextExports = await apiJson<{ exports: ExportRecord[] }>("/api/exports");
      setExports(nextExports.exports);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setMe(null);
        if (embeddedInShopify) {
          setError("Unable to authenticate this Shopify admin session. Refresh the app or reopen it from Shopify Admin.");
        }
      } else {
        setError(caught instanceof Error ? caught.message : "Unable to load app data.");
      }
    } finally {
      if (!options.silent) setLoading(false);
    }
  }

  async function refreshIssues() {
    const scanId = activeScan?.id;
    if (!scanId) return;
    const [nextIssues, nextFacets] = await Promise.all([
      fetchIssues(scanId, severity, issueType, status, query),
      apiJson<{ facets: IssueFacets }>(`/api/issues/facets?scanId=${encodeURIComponent(scanId)}&status=${encodeURIComponent(status)}`)
    ]);
    setIssues(nextIssues.issues);
    setIssueFacets(nextFacets.facets);
  }

  async function startScan() {
    setStarting(true);
    setError(null);
    try {
      const result = await apiJson<{ scan: ScanJob; alreadyRunning?: boolean }>("/api/scans", { method: "POST" });
      setScans((current) => (
        result.alreadyRunning
          ? current.map((scan) => scan.id === result.scan.id ? result.scan : scan)
          : [result.scan, ...current]
      ));
      setMe((current) => current && { ...current, latestScan: result.scan });
      window.shopify?.toast?.show(result.alreadyRunning ? "A scan is already running" : "Scan started");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to start scan.";
      setError(message);
      if (caught instanceof ApiError && caught.status === 402) setView("billing");
      window.shopify?.toast?.show(message, { isError: true });
    } finally {
      setStarting(false);
    }
  }

  async function createExport() {
    if (!activeScan) return;
    try {
      const result = await apiJson<{ export: ExportRecord }>(`/api/scans/${activeScan.id}/export`, { method: "POST" });
      setExports((current) => [result.export, ...current]);
      window.shopify?.toast?.show("CSV export is ready");
      await downloadExport(result.export.id);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to export CSV.";
      setError(message);
      if (caught instanceof ApiError && caught.status === 402) setView("billing");
      window.shopify?.toast?.show(message, { isError: true });
    }
  }

  async function downloadExport(exportId: string) {
    try {
      await downloadFile(`/api/exports/${exportId}/download`);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to download CSV.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
    }
  }

  async function saveSettings() {
    setSavingSettings(true);
    setError(null);
    try {
      const result = await apiJson<{ settings: FeedMappingSettings }>("/api/settings", {
        method: "PUT",
        body: JSON.stringify({ settings: settingsDraft })
      });
      setSettings(result.settings);
      setSettingsDraft(result.settings);
      window.shopify?.toast?.show("Settings saved");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to save settings.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
    } finally {
      setSavingSettings(false);
    }
  }

  async function updateIssueStatus(issueId: string, nextStatus: Issue["status"]) {
    try {
      const result = await apiJson<{ issue: Issue }>(`/api/issues/${issueId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus })
      });
      setIssues((current) => (
        status === "all"
          ? current.map((issue) => issue.id === issueId ? result.issue : issue)
          : current.filter((issue) => issue.id !== issueId)
      ));
      window.shopify?.toast?.show(nextStatus === "open" ? "Issue reopened" : `Issue marked ${nextStatus}`);
      void refreshIssues();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to update issue.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
    }
  }

  async function subscribeToPro() {
    setSubscribing(true);
    setError(null);
    try {
      const result = await apiJson<{
        confirmationUrl: string | null;
        billing: BillingSummary;
        alreadySubscribed: boolean;
        approvalPending: boolean;
        created: boolean;
      }>(
        "/api/billing/subscribe",
        { method: "POST", body: JSON.stringify({ plan: "pro" }) }
      );
      setMe((current) => current && { ...current, billing: result.billing });
      if (!result.confirmationUrl) {
        window.shopify?.toast?.show(result.approvalPending ? "Pro approval is already pending" : "Pro is already active");
        await refreshAll({ silent: true });
        return;
      }
      window.open(result.confirmationUrl, "_top");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to start billing approval.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
    } finally {
      setSubscribing(false);
    }
  }

  function openAppPricingPlanSelection() {
    const planSelectionUrl = me?.billing.planSelectionUrl;
    if (!planSelectionUrl) {
      const message = "Shopify App Pricing plan selection is not configured.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
      return;
    }

    window.open(planSelectionUrl, "_top");
  }

  async function cancelPro() {
    setCancelingSubscription(true);
    setError(null);
    try {
      const result = await apiJson<{ billing: BillingSummary }>("/api/billing/cancel", { method: "POST" });
      setMe((current) => current && { ...current, billing: result.billing });
      window.shopify?.toast?.show("Subscription canceled");
      await refreshAll({ silent: true });
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Unable to cancel subscription.";
      setError(message);
      window.shopify?.toast?.show(message, { isError: true });
    } finally {
      setCancelingSubscription(false);
    }
  }

  if (!embeddedInShopify && installShop) {
    return <PublicRedirectPage />;
  }

  if (!me?.shop && !loading && embeddedInShopify) {
    return (
      <>
        <ui-title-bar title="GMC Feed Preflight" />
        <Page title="GMC Feed Preflight">
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Banner title="Shopify session is not ready" tone="critical">
                    <p>{error ?? "The app could not complete the embedded Shopify session."}</p>
                  </Banner>
                  <InlineStack>
                    <Button variant="primary" onClick={() => void refreshAll()} loading={loading}>
                      Retry
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      </>
    );
  }

  if (!me?.shop && !loading) {
    return <PublicAppPage />;
  }

  return (
    <>
      <ui-title-bar title="GMC Feed Preflight" />
      <Page
        title="GMC Feed Preflight"
        subtitle={me?.shop ? `${me.shop.domain}${me.shop.currency ? ` · ${me.shop.currency}` : ""}` : undefined}
        primaryAction={{ content: "Start scan", onAction: startScan, loading: starting, disabled: isScanning }}
        secondaryActions={[{ content: "Refresh", onAction: () => void refreshAll() }]}
      >
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              {error && (
                <Banner title="Something needs attention" tone="critical" onDismiss={() => setError(null)}>
                  <p>{error}</p>
                </Banner>
              )}

              <nav className="tabs" aria-label="Primary views">
                {(["dashboard", "issues", "exports", "settings", "billing"] as View[]).map((item) => (
                  <button
                    key={item}
                    className={view === item ? "tab active" : "tab"}
                    onClick={() => setView(item)}
                    type="button"
                  >
                    {labelForView(item)}
                  </button>
                ))}
              </nav>

              {view === "dashboard" && (
                <Dashboard
                  activeScan={activeScan}
                  isScanning={isScanning}
                  loading={loading}
                  stats={stats}
                  settings={settings}
                  exportsCount={exports.length}
                  capabilities={me?.capabilities ?? null}
                  billing={me?.billing ?? null}
                  onStartScan={startScan}
                  starting={starting}
                />
              )}

              {view === "issues" && (
                <IssuesView
                  issues={issues}
                  severity={severity}
                  issueType={issueType}
                  issueTypeOptions={issueTypeOptions}
                  status={status}
                  query={query}
                  setSeverity={setSeverity}
                  setIssueType={setIssueType}
                  setStatus={setStatus}
                  setQuery={setQuery}
                  refreshIssues={() => void refreshIssues()}
                  activeScan={activeScan}
                  updateIssueStatus={(issueId, nextStatus) => void updateIssueStatus(issueId, nextStatus)}
                />
              )}

              {view === "exports" && (
                <ExportsView
                  activeScan={activeScan}
                  exports={exports}
                  canExport={me?.billing.limits.canExport ?? false}
                  createExport={() => void createExport()}
                  downloadExport={(exportId) => void downloadExport(exportId)}
                  openBilling={() => setView("billing")}
                />
              )}

              {view === "settings" && (
                <SettingsView
                  settings={settingsDraft}
                  savedSettings={settings}
                  saving={savingSettings}
                  setSettings={setSettingsDraft}
                  saveSettings={() => void saveSettings()}
                />
              )}

              {view === "billing" && me?.billing && (
                <BillingView
                  billing={me.billing}
                  subscribing={subscribing}
                  canceling={cancelingSubscription}
                  subscribeToPro={() => void subscribeToPro()}
                  openAppPricingPlanSelection={openAppPricingPlanSelection}
                  cancelPro={() => void cancelPro()}
                />
              )}
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>
    </>
  );
}

function Dashboard(props: {
  activeScan: ScanJob | null;
  isScanning: boolean;
  loading: boolean;
  stats: { critical: number; warning: number; info: number };
  settings: FeedMappingSettings | null;
  exportsCount: number;
  capabilities: MeResponse["capabilities"] | null;
  billing: BillingSummary | null;
  onStartScan: () => void;
  starting: boolean;
}) {
  const progress = props.activeScan?.status === "completed" ? 100 : props.isScanning ? 42 : 0;

  return (
    <BlockStack gap="400">
      <section className="metricGrid" aria-label="Scan summary">
        <Metric label="Critical" value={props.stats.critical} tone="critical" />
        <Metric label="Warnings" value={props.stats.warning} tone="warning" />
        <Metric label="Info" value={props.stats.info} tone="info" />
        <Metric label="Products" value={props.activeScan?.total_products ?? 0} tone="neutral" />
      </section>

      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">Latest scan</Text>
              <Text as="p" tone="subdued">
                Read-only checks for prices, inventory, identifiers, images, and apparel attributes.
              </Text>
            </BlockStack>
            <ScanStatusBadge status={props.activeScan?.status ?? "queued"} />
          </InlineStack>

          {props.isScanning && <ProgressBar progress={progress} tone="primary" />}

          <div className="scanDetails">
            <Detail label="Products" value={props.activeScan?.total_products ?? 0} />
            <Detail label="Variants" value={props.activeScan?.total_variants ?? 0} />
            <Detail label="Issues" value={props.activeScan?.total_issues ?? 0} />
            <Detail label="Mode" value={labelForScanMode(props.activeScan)} />
            <Detail label="Bulk objects" value={props.activeScan?.bulk_object_count ?? 0} />
            <Detail label="Updated" value={props.activeScan ? formatDate(props.activeScan.updated_at) : "Never"} />
          </div>

          {props.activeScan?.error_message && (
            <Banner title="Scan failed" tone="critical">
              <p>{props.activeScan.error_message}</p>
            </Banner>
          )}

          {!props.activeScan && !props.loading && (
            <Box paddingBlockStart="200">
              <Button variant="primary" onClick={props.onStartScan} loading={props.starting}>
                Run first scan
              </Button>
            </Box>
          )}
        </BlockStack>
      </Card>

      <ReadinessPanel
        activeScan={props.activeScan}
        stats={props.stats}
        settings={props.settings}
        exportsCount={props.exportsCount}
        capabilities={props.capabilities}
        billing={props.billing}
      />
    </BlockStack>
  );
}

function ReadinessPanel(props: {
  activeScan: ScanJob | null;
  stats: { critical: number; warning: number; info: number };
  settings: FeedMappingSettings | null;
  exportsCount: number;
  capabilities: MeResponse["capabilities"] | null;
  billing: BillingSummary | null;
}) {
  const settings = props.settings;
  const mappingCount = settings
    ? (Object.keys(FIELD_LABELS) as FeedFieldKey[]).reduce((total, key) => total + settings[key].length, 0)
    : 0;
  const items = [
    {
      label: "Bulk scan",
      done: props.capabilities?.scanMode === "bulk",
      detail: props.activeScan?.bulk_operation_status ?? "Ready"
    },
    {
      label: "Read-only access",
      done: props.capabilities?.readOnly === true,
      detail: "No product writes"
    },
    {
      label: "Billing",
      done: props.capabilities?.supportsBilling === true,
      detail: props.billing?.plan === "pro" ? "Pro active" : "Free plan"
    },
    {
      label: "Attribute mapping",
      done: mappingCount > 0,
      detail: `${mappingCount} sources`
    },
    {
      label: "Latest scan",
      done: props.activeScan?.status === "completed",
      detail: props.activeScan ? capitalize(props.activeScan.status) : "Not run"
    },
    {
      label: "Critical review",
      done: props.activeScan?.status === "completed" && props.stats.critical === 0,
      detail: `${props.stats.critical} open`
    },
    {
      label: "CSV handoff",
      done: props.exportsCount > 0,
      detail: `${props.exportsCount} exports`
    }
  ];
  const readyCount = items.filter((item) => item.done).length;

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">Commercial readiness</Text>
            <Text as="p" tone="subdued">
              Operational checks for running this app with real merchant catalogs.
            </Text>
          </BlockStack>
          <Badge tone={items.every((item) => item.done) ? "success" : "attention"}>
            {`${readyCount}/${items.length}`}
          </Badge>
        </InlineStack>
        <div className="readinessGrid">
          {items.map((item) => (
            <div className="readinessItem" key={item.label}>
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" fontWeight="semibold">{item.label}</Text>
                <Badge tone={item.done ? "success" : "attention"}>{item.done ? "Ready" : "Open"}</Badge>
              </InlineStack>
              <Text as="p" tone="subdued">{item.detail}</Text>
            </div>
          ))}
        </div>
      </BlockStack>
    </Card>
  );
}

function IssuesView(props: {
  issues: Issue[];
  severity: string;
  issueType: string;
  issueTypeOptions: Array<{ issue_type: string; count: number }>;
  status: string;
  query: string;
  setSeverity: (value: string) => void;
  setIssueType: (value: string) => void;
  setStatus: (value: string) => void;
  setQuery: (value: string) => void;
  refreshIssues: () => void;
  activeScan: ScanJob | null;
  updateIssueStatus: (issueId: string, status: Issue["status"]) => void;
}) {
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">Issues</Text>
            <Text as="p" tone="subdued">
              Sorted by severity so operators can fix the highest-risk fields first.
            </Text>
          </BlockStack>
          <Button onClick={props.refreshIssues}>Apply filters</Button>
        </InlineStack>

        <div className="filters">
          <label>
            <span>Severity</span>
            <select value={props.severity} onChange={(event) => props.setSeverity(event.target.value)}>
              <option value="all">All</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
            </select>
          </label>
          <label>
            <span>Issue type</span>
            <select value={props.issueType} onChange={(event) => props.setIssueType(event.target.value)}>
              <option value="all">All</option>
              {props.issueTypeOptions.map((item) => (
                <option key={item.issue_type} value={item.issue_type}>
                  {labelForIssueType(item.issue_type)} ({item.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select value={props.status} onChange={(event) => props.setStatus(event.target.value)}>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="ignored">Ignored</option>
              <option value="all">All</option>
            </select>
          </label>
          <TextField
            label="Search"
            labelHidden
            placeholder="Product, SKU, field, issue"
            value={props.query}
            onChange={props.setQuery}
            autoComplete="off"
          />
        </div>

        {!props.activeScan && <Empty title="No scan yet" body="Run a scan to populate product and variant issues." />}
        {props.activeScan && props.issues.length === 0 && <Empty title="No issues found" body="This filter has no matching issues." />}
        {props.issues.length > 0 && <IssuesTable issues={props.issues} updateIssueStatus={props.updateIssueStatus} />}
      </BlockStack>
    </Card>
  );
}

function ExportsView(props: {
  activeScan: ScanJob | null;
  exports: ExportRecord[];
  canExport: boolean;
  createExport: () => void;
  downloadExport: (exportId: string) => void;
  openBilling: () => void;
}) {
  const scanCompleted = props.activeScan?.status === "completed";
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">CSV exports</Text>
            <Text as="p" tone="subdued">
              Download a remediation list for operators, agencies, or bulk editing workflows.
            </Text>
          </BlockStack>
          <Button variant="primary" disabled={!scanCompleted} onClick={props.canExport ? props.createExport : props.openBilling}>
            Export CSV
          </Button>
        </InlineStack>

        {!props.canExport && (
          <Banner title="CSV exports are included with Pro" tone="info">
            <p>Upgrade when you need to hand off issue lists to an operator, agency, or bulk editor.</p>
          </Banner>
        )}
        {props.exports.length === 0 && <Empty title="No exports yet" body="Complete a scan, then export its issue list as CSV." />}
        {props.exports.length > 0 && (
          <div className="exportList">
            {props.exports.map((item) => (
              <div className="exportRow" key={item.id}>
                <div>
                  <Text as="p" fontWeight="semibold">{item.filename}</Text>
                  <Text as="p" tone="subdued">{formatDate(item.created_at)}</Text>
                </div>
                <Button onClick={() => props.downloadExport(item.id)}>Download</Button>
              </div>
            ))}
          </div>
        )}
      </BlockStack>
    </Card>
  );
}

function BillingView(props: {
  billing: BillingSummary;
  subscribing: boolean;
  canceling: boolean;
  subscribeToPro: () => void;
  openAppPricingPlanSelection: () => void;
  cancelPro: () => void;
}) {
  const proPlan = props.billing.plans.find((plan) => plan.key === "pro");
  const freePlan = props.billing.plans.find((plan) => plan.key === "free");
  const approvalPending = props.billing.status === "pending";
  const usesAppPricing = props.billing.billingMode === "app_pricing";
  const appPricingUnavailable = usesAppPricing && !props.billing.planSelectionUrl;
  const planBadge = props.billing.plan === "pro"
    ? "Pro active"
    : approvalPending
      ? "Approval pending"
      : "Free";

  return (
    <BlockStack gap="400">
      {props.billing.testMode && (
        <Banner title="Billing test mode is enabled" tone="info">
          <p>Subscription approvals use Shopify test charges in this environment.</p>
        </Banner>
      )}
      {approvalPending && (
        <Banner title="Pro approval is pending" tone="warning">
          <p>Complete the Shopify approval page to activate Pro access. Until approval is completed, Free plan limits still apply.</p>
        </Banner>
      )}

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">Plan</Text>
              <Text as="p" tone="subdued">
                {usesAppPricing
                  ? "Shopify App Pricing handles plan selection and app charges."
                  : "Shopify Billing handles subscription approval and app charges."}
              </Text>
            </BlockStack>
            <Badge tone={props.billing.plan === "pro" ? "success" : "attention"}>
              {planBadge}
            </Badge>
          </InlineStack>

          {props.billing.activeSubscription && (
            <div className="scanDetails">
              <Detail label="Subscription" value={props.billing.activeSubscription.name} />
              <Detail label="Status" value={capitalize(props.billing.activeSubscription.status.toLowerCase())} />
              <Detail label="Trial days" value={props.billing.activeSubscription.trialDays} />
              <Detail label="Renews" value={props.billing.activeSubscription.currentPeriodEnd ? formatDate(props.billing.activeSubscription.currentPeriodEnd) : "Pending"} />
            </div>
          )}
        </BlockStack>
      </Card>

      <div className="pricingGrid">
        {freePlan && (
          <PlanCard
            plan={freePlan}
            active={props.billing.plan === "free" && !approvalPending}
            actionLabel={props.billing.plan === "free" ? "Current plan" : usesAppPricing ? "Manage in Shopify" : "Downgrade to Free"}
            disabled={props.billing.plan === "free" || (usesAppPricing ? appPricingUnavailable : props.canceling || approvalPending)}
            loading={usesAppPricing ? props.subscribing : props.canceling}
            onAction={usesAppPricing ? props.openAppPricingPlanSelection : props.cancelPro}
          />
        )}
        {proPlan && (
          <PlanCard
            plan={proPlan}
            active={props.billing.plan === "pro"}
            highlighted
            actionLabel={props.billing.plan === "pro" ? usesAppPricing ? "Manage in Shopify" : "Current plan" : approvalPending ? "Continue approval" : usesAppPricing ? "Choose Pro in Shopify" : "Start Pro trial"}
            disabled={usesAppPricing ? appPricingUnavailable : props.billing.plan === "pro" || props.subscribing}
            loading={props.subscribing}
            onAction={usesAppPricing ? props.openAppPricingPlanSelection : props.subscribeToPro}
          />
        )}
      </div>
    </BlockStack>
  );
}

function PlanCard(props: {
  plan: BillingPlan;
  active: boolean;
  highlighted?: boolean;
  actionLabel: string;
  disabled: boolean;
  loading: boolean;
  onAction: () => void;
}) {
  return (
    <Card>
      <div className={props.highlighted ? "planCard highlighted" : "planCard"}>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">{props.plan.name}</Text>
            {props.active && <Badge tone="success">Active</Badge>}
          </InlineStack>
          <div>
            <Text as="p" variant="headingLg">
              {props.plan.price === 0 ? "$0" : `$${props.plan.price}`}
            </Text>
            <Text as="p" tone="subdued">
              {props.plan.price === 0 ? "Free forever" : "per month"}
            </Text>
          </div>
          <Text as="p" tone="subdued">
            {props.plan.productLimit ? `Scan up to ${props.plan.productLimit.toLocaleString()} products.` : "No product limit."}
          </Text>
          <ul className="featureList">
            {props.plan.features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
          <Button variant={props.highlighted ? "primary" : "secondary"} disabled={props.disabled} loading={props.loading} onClick={props.onAction}>
            {props.actionLabel}
          </Button>
        </BlockStack>
      </div>
    </Card>
  );
}

function SettingsView(props: {
  settings: FeedMappingSettings;
  savedSettings: FeedMappingSettings | null;
  saving: boolean;
  setSettings: (settings: FeedMappingSettings) => void;
  saveSettings: () => void;
}) {
  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">Google attribute mapping</Text>
            <Text as="p" tone="subdued">
              Each line is checked in order. Use Shopify fields, metafields, tags, or options.
            </Text>
          </BlockStack>
          <InlineStack gap="200">
            <Button onClick={() => props.savedSettings && props.setSettings(props.savedSettings)} disabled={!props.savedSettings || props.saving}>
              Reset
            </Button>
            <Button variant="primary" onClick={props.saveSettings} loading={props.saving}>
              Save settings
            </Button>
          </InlineStack>
        </InlineStack>

        <div className="mappingGrid">
          {(Object.keys(FIELD_LABELS) as FeedFieldKey[]).map((key) => (
            <TextField
              key={key}
              label={FIELD_LABELS[key]}
              helpText={helpForField(key)}
              value={props.settings[key].join("\n")}
              onChange={(value) => props.setSettings({
                ...props.settings,
                [key]: value.split(/\r?\n/).map((source) => source.trim()).filter(Boolean)
              })}
              multiline={3}
              autoComplete="off"
            />
          ))}
        </div>

        <div className="mappingExamples">
          <code>vendor</code>
          <code>variant.metafield.google.mpn</code>
          <code>product.metafield.custom.brand</code>
          <code>tag:color:</code>
          <code>option:Size</code>
        </div>
      </BlockStack>
    </Card>
  );
}

function IssuesTable({ issues, updateIssueStatus }: {
  issues: Issue[];
  updateIssueStatus: (issueId: string, status: Issue["status"]) => void;
}) {
  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>Severity</th>
            <th>Product</th>
            <th>Variant / SKU</th>
            <th>Field</th>
            <th>Status</th>
            <th>Issue</th>
            <th>Suggested fix</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => (
            <tr key={issue.id}>
              <td><SeverityBadge severity={issue.severity} /></td>
              <td>{issue.product_title ?? "Shop"}</td>
              <td>{issue.variant_title ?? issue.sku ?? "-"}</td>
              <td><code>{issue.field}</code></td>
              <td><StatusBadge status={issue.status} /></td>
              <td>{issue.message}</td>
              <td>{issue.suggested_fix}</td>
              <td>
                <InlineStack gap="100">
                  {issue.status !== "resolved" && (
                    <Button size="slim" onClick={() => updateIssueStatus(issue.id, "resolved")}>Resolve</Button>
                  )}
                  {issue.status !== "ignored" && (
                    <Button size="slim" onClick={() => updateIssueStatus(issue.id, "ignored")}>Ignore</Button>
                  )}
                  {issue.status !== "open" && (
                    <Button size="slim" onClick={() => updateIssueStatus(issue.id, "open")}>Reopen</Button>
                  )}
                </InlineStack>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <Card>
      <div className={`metric ${tone}`}>
        <Text as="p" tone="subdued">{label}</Text>
        <strong>{value}</strong>
      </div>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <Text as="p" tone="subdued">{label}</Text>
      <Text as="p" fontWeight="semibold">{value}</Text>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Issue["severity"] }) {
  const tone = severity === "critical" ? "critical" : severity === "warning" ? "warning" : "info";
  return <Badge tone={tone}>{capitalize(severity)}</Badge>;
}

function StatusBadge({ status }: { status: Issue["status"] }) {
  const tone = status === "resolved" ? "success" : status === "ignored" ? "attention" : "info";
  return <Badge tone={tone}>{capitalize(status)}</Badge>;
}

function ScanStatusBadge({ status }: { status: ScanJob["status"] }) {
  const tone = status === "completed" ? "success" : status === "failed" ? "critical" : "attention";
  return <Badge tone={tone}>{capitalize(status)}</Badge>;
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="empty">
      <Text as="h3" variant="headingSm">{title}</Text>
      <Text as="p" tone="subdued">{body}</Text>
    </div>
  );
}

function PublicAppPage() {
  return (
    <Page title="GMC Feed Preflight">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">Read-only product feed readiness checks</Text>
              <Text as="p" tone="subdued">
                GMC Feed Preflight is an embedded Shopify app for checking product data risks before Google Merchant Center feed submission.
              </Text>
              <Text as="p" tone="subdued">
                Installation must be started from Shopify. Open the app from its Shopify App Store listing or from Shopify Admin after it has been added to a store.
              </Text>
              <InlineStack gap="200">
                <Button url="/privacy">Privacy Policy</Button>
                <Button url="/terms">Terms</Button>
                <Button url="/support">Support</Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function PublicRedirectPage() {
  return (
    <Page title="GMC Feed Preflight">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingLg">Starting Shopify installation</Text>
              <Text as="p" tone="subdued">
                Redirecting to Shopify OAuth for secure app authorization.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function shopFromInstallUrl(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.get("host") || params.get("installed") === "1") return "";
  return params.get("shop") ?? "";
}

function labelForView(view: View): string {
  return view === "dashboard"
    ? "Dashboard"
    : view === "issues"
      ? "Issues"
      : view === "exports"
        ? "Exports"
        : view === "settings"
          ? "Settings"
          : "Billing";
}

function labelForScanMode(scan: ScanJob | null): string {
  if (!scan) return "Bulk";
  if (scan.scan_mode !== "bulk") return "Paginated";
  return scan.bulk_operation_status ? `Bulk ${capitalize(scan.bulk_operation_status.toLowerCase())}` : "Bulk";
}

function labelForIssueType(value: string): string {
  return value.split("_").map(capitalize).join(" ");
}

function emptyIssueFacets(): IssueFacets {
  return {
    severity: { critical: 0, warning: 0, info: 0 },
    issueTypes: []
  };
}

function emptyFeedMappings(): FeedMappingSettings {
  return {
    brand: ["vendor", "product.metafield.google.brand", "product.metafield.custom.brand", "tag:brand:"],
    mpn: ["variant.metafield.google.mpn", "variant.metafield.custom.mpn", "product.metafield.google.mpn"],
    identifierExists: ["variant.metafield.google.identifier_exists", "product.metafield.google.identifier_exists", "tag:identifier_exists:"],
    color: ["option:color", "product.metafield.google.color", "tag:color:"],
    size: ["option:size", "product.metafield.google.size", "tag:size:"],
    gender: ["product.metafield.google.gender", "tag:gender:"],
    ageGroup: ["product.metafield.google.age_group", "tag:age_group:"]
  };
}

function helpForField(key: FeedFieldKey): string {
  switch (key) {
    case "brand":
      return "Usually vendor, brand metafield, or brand tag.";
    case "mpn":
      return "Usually a variant or product metafield.";
    case "identifierExists":
      return "Use true/false from a metafield or tag prefix.";
    case "color":
    case "size":
      return "Usually a variant option, metafield, or tag prefix.";
    case "gender":
    case "ageGroup":
      return "Usually a product metafield or tag prefix.";
  }
}

function fetchIssues(scanId: string, severity: string, issueType: string, status: string, query: string): Promise<{ issues: Issue[] }> {
  const params = new URLSearchParams({
    scanId,
    severity,
    issueType,
    status,
    query
  });
  return apiJson<{ issues: Issue[] }>(`/api/issues?${params.toString()}`);
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}
